import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuditLogTypes } from "@constants/audit-log-types";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { useDeviceDetector } from "@hooks/use-device-detector";
import { getSdkStateSelector } from "@redux/selectors/sdk-selectors";
import {
    getUseAcuantSDKCapture,
    getAcuantDisableCamera,
    getAsi,
    getAPIKey
} from "@redux/selectors/verification-configuration-selectors";
import { getVerificationStepConfigState } from "@redux/selectors/verification-step-config-selectors";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { CompletePage } from "../complete-page/complete-page";
import { DriverLicenseBackPage } from "../driver-license-back-page/driver-license-back-page";
import { DriverLicenseFrontPage } from "../driver-license-front-page/driver-license-front-page";
import { IdCardFrontPage } from "../id-card-front-page/id-card-front-page";
import { IdCardBackPage } from "../id-card-back-page/id-card-back-page";
import { ErrorPage } from "../error-page/error-page";
import { FacePage } from "../face-page/face-page";
import { PassportPage } from "../passport-page/passport-page";
import { StartPage } from "../start-page/start-page";
import { HiddenValueInput } from "../common/hidden-value-input/hidden-value-input";
import styles from "./main-container.less";

declare global {
    interface Window {
        idwPageHasLoaded: boolean;
    }
}

export const MainContainer = () => {
    const deviceInfo = useDeviceDetector();
    const [logDeviceInfo, setLogDeviceInfo] = useState(true);
    const { writeAuditLog } = useAuditLogger();
    const queryParametersState = useSelector(getQueryParametersState);
    const useAcuantSDKCapture = useSelector(getUseAcuantSDKCapture);
    const acuantDisableCamera = useSelector(getAcuantDisableCamera);
    const asi = useSelector(getAsi);
    const apikey = useSelector(getAPIKey);
    const verificationStepConfigState = useSelector(getVerificationStepConfigState);
    const sdkState = useSelector(getSdkStateSelector);

    const generateAuditLog = () => {
        setLogDeviceInfo(false);
        if (typeof deviceInfo?.device?.type === "undefined") {
            if (useAcuantSDKCapture) {
                AcuantCamera.isCameraSupported = false;
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_DESKTOP_MANUAL_CAPTURE_SET);
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_DESKTOP_MANUAL_CAPTURE_SET);
            }
        } else if (useAcuantSDKCapture && acuantDisableCamera) {
            AcuantCamera.isCameraSupported = false;
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_MOBILE_MANUAL_CAPTURE_SET);
        } else if (useAcuantSDKCapture) {
            AcuantCamera.isCameraSupported = true;
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_MOBILE_LIVE_CAPTURE_SET);
        } else {
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_MOBILE_MANUAL_CAPTURE_SET);
        }
    };

    useEffect(() => {
        window.idwPageHasLoaded = false;

        const onPageLoad = () => {
            window.idwPageHasLoaded = true;
        };

        // Check if the page has already loaded
        if (document.readyState === 'complete') {
            return onPageLoad();
        } else {
            window.addEventListener('load', onPageLoad);
            // Remove the event listener when component unmounts
            return () => window.removeEventListener('load', onPageLoad);
        }
    }, []);

    useEffect(() => {
        if (
            (typeof deviceInfo?.device?.type === "undefined" || deviceInfo?.device?.type !== "") &&
            (verificationStepConfigState.initialized) &&
            (sdkState.hasDownloadedAcuantSDK || !useAcuantSDKCapture) &&
            logDeviceInfo
        ) {
            generateAuditLog();
        }
    }, [
        deviceInfo,
        verificationStepConfigState.initialized,
        sdkState.hasDownloadedAcuantSDK,
        useAcuantSDKCapture
    ]);

    return (
        <div id={"main-container"} className={styles.mainContainer}>
            <div id={"form-panel"} className={styles.formPanel}>
                <Routes>
                    <Route path={"/dcui/error"} element={<ErrorPage />} />
                    <Route path={"/dcui/start"} element={<StartPage />} />
                    <Route path={"/dcui/complete"} element={<CompletePage />} />
                    <Route path={"/dcui/driver-license/front"} element={<DriverLicenseFrontPage />} />
                    <Route path={"/dcui/driver-license/back"} element={<DriverLicenseBackPage />} />
                    <Route path={"/dcui/id-card/front"} element={<IdCardFrontPage />} />
                    <Route path={"/dcui/id-card/back"} element={<IdCardBackPage />} />
                    <Route path={"/dcui/face"} element={<FacePage />} />
                    <Route path={"/dcui/passport"} element={<PassportPage />} />
                    <Route path={"/dcui"} element={<Navigate to={"/dcui/start"} replace />} />
                    <Route path={"/"} element={<Navigate to={"/dcui/start"} replace />} />
                </Routes>
            </div>
            <HiddenValueInput id={"dcui-token"} value={queryParametersState?.token} name={"dcui-token"} />
            <HiddenValueInput id={"service-asi"} value={asi || ""} name={"asi"} />
			<HiddenValueInput id={"service-apikey"} value={apikey} name={"apikey"} />
			<HiddenValueInput id={"dcui-version"} value={process.env.REACT_APP_VERSION} name={"dcui-version"} />
        </div>
    );
};